import { APP_INITIALIZER, Provider } from '@angular/core';
import { BrandingService } from '../../shared/services/branding/branding.service';
import { UserService } from '../services/user.service';
import { AuthService } from '../auth/auth.service';
import { firstValueFrom } from 'rxjs';
import { filter, timeout } from 'rxjs/operators';

/**
 * Branding Initializer - HTTP-Only with Auth Wait
 * 
 * Waits for Auth0 authentication to complete, then loads branding from API.
 * This ensures the bearer token is available for authenticated API calls.
 * 
 * Flow:
 * 1. Wait for Auth0 to finish loading (isLoading$ = false)
 * 2. Get current user's organization ID
 * 3. Load branding from /api/branding endpoint
 * 4. Apply branding site-wide via CSS variables
 */
export function initializeBranding(
    brandingService: BrandingService,
    userService: UserService,
    authService: AuthService
): () => Promise<void> {
    return async () => {
        try {
            console.log(`[BrandingInitializer] ⏳ Waiting for authentication...`);

            // Wait for Auth0 to finish loading (max 5 seconds)
            await firstValueFrom(
                authService.isLoading$.pipe(
                    filter(isLoading => !isLoading),
                    timeout(5000)
                )
            ).catch(() => {
                console.warn(`[BrandingInitializer] Auth timeout, proceeding anyway...`);
            });

            console.log(`[BrandingInitializer] ✓ Auth ready`);

            // Get current user
            const user = userService.getCurrentUser();

            // Load branding based on current domain (not organization)
            console.log(`[BrandingInitializer] 🎨 Loading branding for current domain`);

            try {
                // Load branding from HTTP API based on domain
                const config = await firstValueFrom(brandingService.loadBranding());

                if (config) {
                    console.log(`[BrandingInitializer] ✅ Branding loaded from API for domain: ${config.organizationId}`);

                    // Apply branding to entire site
                    brandingService.applyBrandingToDOM(config);

                    console.log(`[BrandingInitializer] 🎨 Site-wide branding applied!`, {
                        header: config.colors.header,
                        button: config.colors.button
                    });
                } else {
                    console.log(`[BrandingInitializer] ⚠️ No branding config found`);
                }
            } catch (error: any) {
                console.warn(`[BrandingInitializer] ⚠️ Failed to load from API:`, error?.message || error);
                console.log(`[BrandingInitializer] Site will use default styling`);
            }

            if (!user?.organizationId) {
                console.log(`[BrandingInitializer] ℹ️ No user organization`);
            }
        } catch (error: any) {
            console.error(`[BrandingInitializer] ❌ Initialization error:`, error);
        }
    };
}

/**
 * Provider for branding initialization
 */
export const brandingInitializerProvider: Provider = {
    provide: APP_INITIALIZER,
    useFactory: initializeBranding,
    deps: [BrandingService, UserService, AuthService],
    multi: true
};
